import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { routes } from '~/constants';
import { useIoContext, useOverlayContext } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { sendErrReport } from '~/infra/utils';
export function useNativeOAuthCallback() {
    const { cookies, localStorage, isNativePlatform, browser } = useIoContext();
    const { showErrorToast } = useOverlayContext();
    const { t: commonTexts } = useLocaleKeys('common');
    const history = useHistory();
    const enabled = isNativePlatform();
    useEffect(()=>{
        if (!enabled) return;
        let lastProcessedState = null;
        let cancelled = false;
        let teardown;
        const showProviderErrorToast = (provider)=>{
            const message = provider === 'qbo' ? commonTexts.toasts.failedToContinueWithIntuit() : commonTexts.toasts.failedToContinueWithXero();
            showErrorToast(message, "native-oauth-".concat(provider, "-error-toast"));
        };
        void (async ()=>{
            const { getOAuthLaunchUrl, parseOAuthCallback, subscribeToOAuthCallback } = await import('~/native/oauth-callback-listener');
            const { clearPendingOAuthState, readPendingOAuthState } = await import('~/native/oauth-state-storage');
            const handleResult = (result)=>{
                if (result.kind === 'ignored') {
                    return;
                }
                if (result.kind === 'error') {
                    showProviderErrorToast(result.provider);
                    return;
                }
                if (lastProcessedState === result.state) return;
                const expectedState = readPendingOAuthState(localStorage, result.provider);
                if (expectedState == null || expectedState !== result.state) {
                    sendErrReport(new Error("native-oauth-state-mismatch: ".concat(result.provider)));
                    showProviderErrorToast(result.provider);
                    return;
                }
                try {
                    cookies.setAuthTokenForNative(result.authToken);
                } catch (err) {
                    sendErrReport(err);
                    showProviderErrorToast(result.provider);
                    return;
                }
                clearPendingOAuthState(localStorage, result.provider);
                browser.scrubUrlFragment();
                lastProcessedState = result.state;
                history.push(routes.home);
            };
            const subscription = await subscribeToOAuthCallback(handleResult);
            if (cancelled) {
                subscription();
                return;
            }
            teardown = subscription;
            const launchUrl = await getOAuthLaunchUrl();
            if (launchUrl != null && !cancelled) {
                handleResult(parseOAuthCallback(launchUrl));
            }
        })();
        return ()=>{
            cancelled = true;
            teardown === null || teardown === void 0 ? void 0 : teardown();
        };
    }, [
        enabled
    ]);
}
export function NativeOAuthCallbackEffect() {
    useNativeOAuthCallback();
    return null;
}
